import React, { Suspense } from 'react';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/providers/AuthProvider';
import { AuthGuard } from '@/components/common/AuthGuard';
import { NotFound } from '@/pages/NotFound';
import { PerformanceProvider } from '@/components/performance/PerformanceProvider';
import {
  AccessibilityProvider,
  AccessibilityToolbar,
} from '@/components/accessibility/AccessibilityProvider';
import { SecurityProvider } from '@/components/security/SecurityProvider';
import { PWAProvider } from '@/components/pwa/PWAProvider';
import AdvancedErrorBoundary from '@/components/error/AdvancedErrorBoundary';
import { MobileBottomNav } from '@/components/Navigation/MobileBottomNav';
import { DesktopSidebar } from '@/components/Navigation/DesktopSidebar';
import { SidebarProvider, useSidebar } from '@/components/Navigation/SidebarContext';
import { OnboardingProvider } from '@/components/Onboarding/OnboardingProvider';
import { OnboardingTour } from '@/components/Onboarding/OnboardingTour';
import { PageSkeleton } from '@/components/ui/page-skeleton';
import { navItems } from './nav-items';
import { useTokenizedTheme } from '@/hooks/useTokenizedTheme';
import { cn } from '@/lib/utils';

const queryClient = new QueryClient();

/** Routes that render full-width without the sidebar (pure marketing/auth pages).
 * Per convoy-011: use "sidebar or top nav" — home (marketing) keeps its top Navigation header (persistent on desktop),
 * app pages use DesktopSidebar (persistent md+). This preserves hero impact while delivering recovery nav.
 * Modern best practice: context-aware persistent navigation without breaking marketing flows.
 */
const FULL_WIDTH_ROUTES = new Set(['/', '/auth', '/auth/callback', '/auth/reset']);

/**
 * Inner layout component that reads sidebar state to set proper content offset.
 * Full-width pages (homepage, auth) skip the sidebar entirely.
 */
function AppLayout() {
  const { collapsed } = useSidebar();
  const location = useLocation();
  const isFullWidth = FULL_WIDTH_ROUTES.has(location.pathname);

  return (
    <>
      {!isFullWidth && <DesktopSidebar />}
      <div
        className={cn(
          'pb-mobile-nav transition-all duration-200',
          !isFullWidth && (collapsed ? 'md:pl-16' : 'md:pl-60')
        )}
      >
        <Routes>
          {navItems.map(({ to, page, access }) => {
            const element = <Suspense fallback={<PageSkeleton />}>{page}</Suspense>;
            if (access === 'public' || access === 'trial') {
              return <Route key={to} path={to} element={element} />;
            }
            if (access === 'admin') {
              return (
                <Route
                  key={to}
                  path={to}
                  element={<AuthGuard requireRole="super_user">{element}</AuthGuard>}
                />
              );
            }
            return <Route key={to} path={to} element={<AuthGuard>{element}</AuthGuard>} />;
          })}
          {/* Catch-all route for 404 pages */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      {!isFullWidth && <MobileBottomNav />}
    </>
  );
}

const App = () => {
  // Apply design system tokens
  useTokenizedTheme();

  // Initialize self-healing system (only with full monitoring)
  React.useEffect(() => {
    if (import.meta.env.VITE_ENABLE_FULL_MONITORING === 'true') {
      import('@/services/PlatformSelfHealingIntegration').then(({ platformSelfHealing }) => {
        platformSelfHealing.initialize().catch(console.error);
      });
    }
  }, []);

  return (
    <AdvancedErrorBoundary enableErrorReporting={true}>
      <PerformanceProvider enableAutoTracking={true}>
        <AccessibilityProvider>
          <SecurityProvider>
            <PWAProvider>
              <QueryClientProvider client={queryClient}>
                <TooltipProvider>
                  <AuthProvider>
                    <OnboardingProvider>
                      <SidebarProvider>
                        <Toaster />
                        <AccessibilityToolbar />
                        <BrowserRouter>
                          <OnboardingTour />
                          <AppLayout />
                        </BrowserRouter>
                      </SidebarProvider>
                    </OnboardingProvider>
                  </AuthProvider>
                </TooltipProvider>
              </QueryClientProvider>
            </PWAProvider>
          </SecurityProvider>
        </AccessibilityProvider>
      </PerformanceProvider>
    </AdvancedErrorBoundary>
  );
};

export default App;
